import { chapterById, chapters, chapterSeconds, formatDuration, sceneById, scenes, scenesInChapter } from '../content'
import type { Scene } from '../content'
import { Visual } from './visuals'
import { ParcelField } from './visuals/diagrams'
import { Callouts, ExploreDetails, RfpAlignmentDrawer, SourceNote, WhyItMatters } from './SceneParts'
import { LamsIcon } from './Brand'
import { SceneRail } from './SceneRail'

export function Header({
  path,
  onStartPresentation,
}: {
  path: string
  onStartPresentation: () => void
}) {
  const navChapters = chapters.filter((c) => c.navLabel)
  return (
    <header className="masthead">
      <div className="shell masthead__inner">
        <a href="/" className="brand" aria-label="LAMS — return to the opening page">
          <LamsIcon size={30} />
          <span className="brand__text">
            <span className="brand__name">LAMS</span>
            <span className="brand__sub">Land Acquisition &amp; Management System</span>
          </span>
        </a>

        <nav className="mainnav" aria-label="Primary">
          <ul>
            {navChapters.map((c) => {
              const active = path.startsWith(c.route)
              return (
                <li key={c.id}>
                  <a href={c.route} aria-current={active ? 'page' : undefined} className={active ? 'is-active' : ''}>
                    {c.navLabel}
                  </a>
                </li>
              )
            })}
          </ul>
        </nav>

        <button type="button" className="present-btn" onClick={onStartPresentation}>
          <span className="present-btn__icon" aria-hidden="true">
            <svg viewBox="0 0 14 14" width="14" height="14" fill="none">
              <path d="M3 2l9 5-9 5V2z" fill="currentColor" />
            </svg>
          </span>
          Presentation Mode
        </button>
      </div>
    </header>
  )
}

export function SceneArticle({ scene, index }: { scene: Scene; index: number }) {
  const chapter = chapterById.get(scene.chapter)
  return (
    <article className="scene" id={scene.id}>
      <div className="scene__head">
        <span className="scene__eyebrow">
          <span className="mono">{scene.id}</span>
          <span className="scene__eyebrow-sep">·</span>
          {chapter?.title}
        </span>
        <h2 className="scene__headline">{scene.headline}</h2>
        {scene.supporting ? <p className="scene__supporting">{scene.supporting}</p> : null}
      </div>

      <div className="scene__visual">
        <Visual id={scene.visual} />
        {scene.conceptualLabel ? (
          <p className="scene__conceptual">Conceptual LAMS Operations Experience — not a production system</p>
        ) : null}
      </div>

      {scene.callouts?.length ? <Callouts items={scene.callouts} /> : null}
      {scene.whyItMatters ? <WhyItMatters text={scene.whyItMatters} /> : null}
      {scene.exploreDetails?.length ? <ExploreDetails details={scene.exploreDetails} /> : null}

      <footer className="scene__foot">
        <SourceNote sources={scene.sourceRefs} />
        {scene.rfpAlignment?.length ? <RfpAlignmentDrawer items={scene.rfpAlignment} /> : null}
      </footer>

      <span className="visually-hidden">Scene {index + 1} of {scenes.length}</span>
    </article>
  )
}

export function ChapterPage({ chapterId }: { chapterId: string }) {
  const chapter = chapterById.get(chapterId as never)
  const chapterScenes = scenesInChapter(chapterId)
  if (!chapter) return null

  return (
    <div className="chapterpage">
      <div className="shell">
        <header className="chapterpage__head">
          <span className="eyebrow">{chapter.navLabel ?? chapter.title}</span>
          <h1 className="chapterpage__title">{chapter.title}</h1>
          {chapter.intro ? <p className="chapterpage__intro">{chapter.intro}</p> : null}
        </header>

        <div className="chapterpage__body">
          <SceneRail scenes={chapterScenes} chapterTitle={chapter.title} chapterId={chapterId} />

          <div className="chapterpage__main">
            {/* Compact in-flow index, for narrow viewports where the rail is hidden. */}
            <nav className="chapterpage__toc" aria-label={`${chapter.title} contents`}>
              <ol>
                {chapterScenes.map((s) => (
                  <li key={s.id}>
                    <a href={s.route}>
                      <span className="mono">{s.id}</span>
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="chapterpage__scenes">
              {chapterScenes.map((s) => (
                <SceneArticle key={s.id} scene={s} index={scenes.indexOf(s)} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function HomePage({ onStartPresentation }: { onStartPresentation: () => void }) {
  const proposition = sceneById.get('CH-01')!
  const navChapters = chapters.filter((c) => c.navLabel)
  return (
    <div className="home">
      <section className="hero">
        <div className="hero__art" aria-hidden="true">
          <ParcelField />
        </div>
        <div className="shell hero__inner">
          <p className="hero__eyebrow">Merlion IT LLC · RFP 26/27-002</p>
          <div className="hero__lockup">
            <LamsIcon size={104} title="LAMS" />
            <span className="hero__type">
              <h1 className="hero__title">LAMS</h1>
              <p className="hero__sub">Land Acquisition &amp; Management System</p>
            </span>
          </div>
          <p className="hero__tagline">Geospatial intelligence for land stewardship</p>
          <p className="hero__lede">
            A purpose-built operational platform for the complete SRWMD land lifecycle.
          </p>
          <div className="hero__actions">
            <a href="/team" className="btn btn--primary">
              Explore the proposal
            </a>
            <button type="button" className="btn btn--ghost" onClick={onStartPresentation}>
              Start presentation
            </button>
          </div>
          <p className="hero__for">
            Prepared for the Suwannee River Water Management District
          </p>
        </div>
      </section>

      <section className="shell home__proposition">
        <SceneArticle scene={proposition} index={scenes.indexOf(proposition)} />
      </section>

      <nav className="shell home__chapters" aria-label="Chapters">
        <h2 className="home__chapters-title">The argument, in {navChapters.length} chapters</h2>
        <ol className="home__chapter-list">
          {navChapters
            .map((c, i) => (
              <li key={c.id}>
                <a href={c.route} className="home__chapter">
                  <span className="home__chapter-index mono">{String(i + 1).padStart(2, '0')}</span>
                  <span className="home__chapter-body">
                    {c.part ? <span className="home__chapter-meta">{c.part}</span> : null}
                    <span className="home__chapter-name">{c.title}</span>
                    <span className="home__chapter-intro">{c.intro}</span>
                    <span className="home__chapter-meta">
                      {scenesInChapter(c.id).length} scenes · {formatDuration(chapterSeconds(c.id))}
                    </span>
                  </span>
                </a>
              </li>
            ))}
        </ol>
      </nav>
    </div>
  )
}

export function Footer() {
  return (
    <footer className="sitefoot">
      <div className="shell sitefoot__inner">
        <div className="sitefoot__brand">
          <LamsIcon size={34} />
          <p className="sitefoot__line">
            <strong>Merlion IT LLC</strong> · Response to RFP No. 26/27-002, Land Acquisition and Management Software
            Solution · Suwannee River Water Management District
            <a href="https://merlionit.com" target="_blank" rel="noreferrer noopener" className="sitefoot__link">
              merlionit.com
            </a>
          </p>
        </div>
        <p className="sitefoot__note">
          Product screens shown are conceptual designs of the proposed solution, not screenshots of a completed
          production system. Figures reflect the submitted commercial offer.
        </p>
      </div>
    </footer>
  )
}
